"use client";

import { useEffect, useCallback, useRef } from "react";
import { useSession } from "next-auth/react";

/**
 * Keeps auth state in sync across tabs using NextAuth session.
 *
 * - On tab visibility change: re-checks session
 * - Calls onChange when auth state changes
 */
export function useAuthSync(onChange?: (loggedIn: boolean) => void) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const { status, update } = useSession();

  const prevStatus = useRef(status);

  useEffect(() => {
    if (prevStatus.current !== status) {
      prevStatus.current = status;
      if (status === "authenticated") {
        onChangeRef.current?.(true);
      } else if (status === "unauthenticated") {
        onChangeRef.current?.(false);
      }
    }
  }, [status]);

  const validateSession = useCallback(async () => {
    try {
      await update();
    } catch {
      // network error — skip
    }
  }, [update]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        validateSession();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    // Periodic validation every 5 minutes
    const interval = setInterval(validateSession, 5 * 60 * 1000);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(interval);
    };
  }, [validateSession]);

  return { validateSession };
}
